import {
  Box, Grid, Stack, Typography, TextField, MenuItem, Chip, Button,
  InputAdornment,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateMasterInfo } from '../../../../redux/pricingWizardSlice';
import { STOREFRONT_PLATFORMS, OMS_PLATFORMS } from '../constants';

export default function IntegrationStackSection() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const m = useSelector((s) => s.pricingWizard.masterInfo);
  const [customDraft, setCustomDraft] = useState('');

  const setType = (t) => dispatch(updateMasterInfo({ integration_type: t }));
  const toggleOms = (name) => {
    const has = (m.oms_platforms || []).includes(name);
    dispatch(updateMasterInfo({
      oms_platforms: has ? m.oms_platforms.filter((x) => x !== name) : [...(m.oms_platforms || []), name],
    }));
  };
  const addCustom = () => {
    const v = customDraft.trim();
    if (!v || (m.custom_oms || []).includes(v)) { setCustomDraft(''); return; }
    dispatch(updateMasterInfo({ custom_oms: [...(m.custom_oms || []), v] }));
    setCustomDraft('');
  };
  const removeCustom = (v) => dispatch(updateMasterInfo({ custom_oms: (m.custom_oms || []).filter((x) => x !== v) }));

  const TYPE_CARDS = [
    { id: 'storefront', icon: StorefrontIcon, title: 'Directly on storefront platform', sub: 'Proship sits on top of the storefront (Shopify / WooCommerce / Magento).' },
    { id: 'oms', icon: WarehouseOutlinedIcon, title: 'Via an OMS', sub: 'An Order Management System sits between the storefront and Proship.' },
  ];

  return (
    <Box>
      <Grid container spacing={1.5}>
        {TYPE_CARDS.map((c) => {
          const checked = m.integration_type === c.id;
          const Icon = c.icon;
          return (
            <Grid item xs={12} md={6} key={c.id}>
              <Box
                onClick={() => setType(c.id)}
                sx={{
                  p: 1.5, borderRadius: 2, cursor: 'pointer', border: 1, height: '100%',
                  borderColor: checked ? 'primary.main' : 'divider',
                  bgcolor: checked ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                  '&:hover': { borderColor: 'primary.light' },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Box sx={{ width: 34, height: 34, borderRadius: '9px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: checked ? alpha(theme.palette.primary.main, 0.12) : 'action.hover' }}>
                    <Icon sx={{ fontSize: 19, color: checked ? 'primary.main' : 'text.secondary' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: checked ? 'primary.main' : 'text.primary' }}>{c.title}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>{c.sub}</Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Storefront → single-select platform */}
      {m.integration_type === 'storefront' && (
        <Box sx={{ mt: 2 }}>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small" fullWidth select label="Storefront platform"
                value={m.storefront_platform || ''}
                onChange={(e) => dispatch(updateMasterInfo({ storefront_platform: e.target.value }))}
              >
                <MenuItem value="">— select —</MenuItem>
                {STOREFRONT_PLATFORMS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* OMS → multi-select + custom */}
      {m.integration_type === 'oms' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="overline" sx={{ fontSize: 9.5, fontWeight: 700, color: 'text.disabled', letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
            OMS platform(s) — select all that apply
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
            {OMS_PLATFORMS.map((name) => {
              const selected = (m.oms_platforms || []).includes(name);
              return (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  icon={selected ? <CheckIcon sx={{ fontSize: '13px !important', color: 'inherit' }} /> : undefined}
                  onClick={() => toggleOms(name)}
                  sx={{
                    fontSize: 11.5, fontWeight: 600, cursor: 'pointer', borderRadius: '8px', border: 1,
                    bgcolor: selected ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                    color: selected ? 'primary.dark' : 'text.secondary',
                    borderColor: selected ? 'primary.main' : 'divider',
                  }}
                />
              );
            })}
          </Stack>

          {/* Custom OMS entries */}
          {(m.custom_oms || []).length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
              {(m.custom_oms || []).map((v) => (
                <Chip key={v} label={v} size="small" onDelete={() => removeCustom(v)} deleteIcon={<CloseIcon />}
                  sx={{ fontSize: 11.5, fontWeight: 600, borderRadius: '8px', bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.dark' }} />
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Add a custom OMS"
              value={customDraft}
              onChange={(e) => setCustomDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
              sx={{ maxWidth: 280 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><AddIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }}
            />
            <Button size="small" variant="outlined" onClick={addCustom} disabled={!customDraft.trim()} sx={{ borderRadius: '8px' }}>
              Add
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
